import { getWeaponSpec } from "./weaponSpecs";

/**
 * Acessórios de arma em `Armas/itens de armas/`.
 * slot: sight | barrel | laser | magazine
 */
export const WEAPON_ATTACHMENT_SLOTS = ["sight", "barrel", "laser", "magazine"];

const RIFLES = ["AK", "LR", "L96", "M249", "M39_Rifle", "BOLT", "SEMI"];
const SMG = ["MP5", "THOMPSON", "SUB"];
const RIFLE_SMG = [...RIFLES, ...SMG];
const SHOTGUN = ["SPAS", "Pump_Shotgun", "Double_Barrel_Shotgun"];
const PISTOL = ["GLOCK", "PYTHON", "M92", "P2"];
const WITH_SIGHT = [...RIFLE_SMG, ...SHOTGUN, ...PISTOL];
const WITH_BARREL = [...RIFLE_SMG, ...SHOTGUN, ...PISTOL];
const WITH_LASER = RIFLE_SMG;
const LONG_RANGE_SIGHT = ["BOLT", "L96", "M249", "M39_Rifle", "LR", "AK", "SEMI"];

/** @type {{ file: string, name: string, slot: string, weapons: string[], priceKey?: string, itensDeArmasOnly?: boolean }[]} */
export const ATTACHMENT_SPECS = [
    {
        file: "Mira 8x.webp",
        name: "Mira 8x",
        slot: "sight",
        weapons: LONG_RANGE_SIGHT,
        priceKey: "8X",
        itensDeArmasOnly: true,
    },
    {
        file: "Mira Holográfica.webp",
        name: "Mira Holográfica",
        slot: "sight",
        weapons: RIFLE_SMG,
        priceKey: "HOLOGRAFICA",
        itensDeArmasOnly: true,
    },
    {
        file: "Mira Telescópica 16x.png",
        name: "Mira Telescópica 16x",
        slot: "sight",
        weapons: LONG_RANGE_SIGHT,
        priceKey: "Mira Telescópica de 16x",
        itensDeArmasOnly: true,
    },
    {
        file: "Freio de Boca.webp",
        name: "Freio de Boca",
        slot: "barrel",
        weapons: WITH_BARREL,
        priceKey: "Muzzle_Brake",
        itensDeArmasOnly: true,
    },
    {
        file: "Acelerador de Cano.webp",
        name: "Acelerador de Cano",
        slot: "barrel",
        weapons: WITH_BARREL,
        priceKey: "Muzzle_Boost",
        itensDeArmasOnly: true,
    },
    {
        file: "Mira Laser.webp",
        name: "Mira Laser",
        slot: "laser",
        weapons: WITH_LASER,
        priceKey: "LASER",
        itensDeArmasOnly: true,
    },
    {
        file: "Lanterna de Arma.avif",
        name: "Lanterna de Arma",
        slot: "laser",
        weapons: WITH_LASER,
        priceKey: "Weapon_Flashlight",
        itensDeArmasOnly: true,
    },
    {
        file: "Carregador Estendido.avif",
        name: "Carregador Estendido",
        slot: "magazine",
        weapons: RIFLE_SMG,
        priceKey: "carregador_estendido",
        itensDeArmasOnly: true,
    },
];

const BY_FILE = new Map(ATTACHMENT_SPECS.map((a) => [a.file.toLowerCase(), a]));

function basename(filePath) {
    return filePath.split(/[/\\]/).pop() || filePath;
}

function labelFromFileName(file) {
    return file
        .replace(/\.(webp|png|jpe?g|gif|avif)$/i, "")
        .replace(/_icon/gi, "")
        .replace(/\./g, " ")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function inferSlotFromFileName(file) {
    const lower = file.toLowerCase();
    if (/carregador|magazine|clip/i.test(lower)) return "magazine";
    if (/laser|lanterna/i.test(lower)) return "laser";
    if (/mira|sight|8x|16x|holo|telesc|scope|optic/i.test(lower)) return "sight";
    if (/silenci|muzzle|cano|boost|brake|boca|recuo|suppress|barrel/i.test(lower)) return "barrel";
    return "sight";
}

function defaultWeaponsForSlot(slot) {
    if (slot === "magazine") return RIFLE_SMG;
    if (slot === "laser") return WITH_LASER;
    if (slot === "barrel") return WITH_BARREL;
    return WITH_SIGHT;
}

function inferAttachmentSpec(filePath) {
    const base = basename(filePath);
    const slot = inferSlotFromFileName(base);
    return {
        file: base,
        name: labelFromFileName(base),
        slot,
        weapons: defaultWeaponsForSlot(slot),
        priceKey: base.replace(/\.[^.]+$/i, ""),
        itensDeArmasOnly: true,
    };
}

/** Caminho dentro de `Armas/itens de armas/` ou `Armas/itens-de-armas/`. */
export function isItensDeArmasPath(filePath) {
    if (!filePath) return false;
    return /itens[- ]de[- ]armas\//i.test(String(filePath).replace(/\\/g, "/"));
}

/** Qualquer ficheiro em `itens de armas/` que não seja arma. */
export function isItensDeArmasAccessoryPath(filePath) {
    if (!isItensDeArmasPath(filePath)) return false;
    return !getWeaponSpec(filePath);
}

/** @deprecated Use isItensDeArmasAccessoryPath */
export function isItensDeArmasAttachmentPath(filePath) {
    return isItensDeArmasAccessoryPath(filePath);
}

/** @param {string} filePath */
export function getAttachmentSpec(filePath) {
    if (!filePath) return null;
    const base = basename(filePath);
    const known = BY_FILE.get(base.toLowerCase());
    if (known) return known;
    if (isItensDeArmasAccessoryPath(filePath)) {
        return inferAttachmentSpec(filePath);
    }
    return null;
}

/** @param {string} weaponKey */
export function getAttachmentSlotsForWeapon(weaponKey) {
    if (!weaponKey) return [];
    const slots = [];
    if (ATTACHMENT_SPECS.some((a) => a.slot === "sight" && a.weapons.includes(weaponKey))) slots.push("sight");
    if (ATTACHMENT_SPECS.some((a) => a.slot === "barrel" && a.weapons.includes(weaponKey))) slots.push("barrel");
    if (ATTACHMENT_SPECS.some((a) => a.slot === "laser" && a.weapons.includes(weaponKey))) slots.push("laser");
    if (ATTACHMENT_SPECS.some((a) => a.slot === "magazine" && a.weapons.includes(weaponKey))) slots.push("magazine");
    return slots;
}

/**
 * @param {{ cat?: string, iconFile?: string, attachmentSlot?: string, compatibleWeapons?: string[] }} entry
 * @param {string} weaponKey
 */
export function attachmentFitsWeapon(entry, weaponKey) {
    if (!entry || entry.cat !== "attachment" || !weaponKey) return false;
    const slot = entry.attachmentSlot;
    if (!slot) return false;
    const base = (entry.iconFile || "").split(/[/\\]/).pop() || "";
    const spec = ATTACHMENT_SPECS.find(
        (a) => a.slot === slot && (entry.iconFile?.endsWith(a.file) || base.toLowerCase() === a.file.toLowerCase()),
    );
    if (!spec) {
        return (entry.compatibleWeapons || []).includes(weaponKey);
    }
    return spec.weapons.includes(weaponKey);
}

export function isArmasAttachmentFile(filePath) {
    return !!getAttachmentSpec(filePath);
}

/** Entrada do catálogo cujo ícone vem de `itens de armas/`. */
export function isItensDeArmasCatalogEntry(entry) {
    if (!entry) return false;
    const iconFile = entry.iconFile || "";
    const id = entry.id || "";
    return iconFile.includes("itens-de-armas") || id.includes("itens de armas");
}

export function createEmptyWeaponAttachments(weaponKey) {
    const attachments = {};
    for (const slot of getAttachmentSlotsForWeapon(weaponKey)) {
        attachments[slot] = null;
    }
    return attachments;
}
